import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14 text-center">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
          <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-lg font-semibold text-foreground-900 mb-2">漫画未找到</h2>
        <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 no-underline">
          返回首页
        </Link>
      </div>
    );
  }

  const categoryMap: Record<string, string> = { hanman: '韩漫', riman: '日漫', guoman: '国漫' };
  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 6);

  const comicSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicSeries',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    url: `https://yaojingdongman.com/comic/${comic.id}`,
    image: comic.cover,
    genre: comic.tags,
    inLanguage: 'zh-CN',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '妖精动漫',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: Math.floor(comic.views / 100),
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} | 妖精动漫免费在线阅读`}
        description={`${comic.title} - ${comic.description} 妖精动漫免费全集在线阅读，下拉式流畅翻页，${comic.author}作品。`}
        keywords={`${comic.title},${comic.author},妖精动漫,${comic.tags.join(',')},${categoryMap[comic.category] || ''}漫画`}
        canonicalUrl={`https://yaojingdongman.com/comic/${comic.id}`}
        ogType="article"
        schema={comicSchema}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryMap[comic.category] || '漫画', path: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        {/* Comic Info */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-10 mb-12">
          <div className="flex-shrink-0 w-full sm:w-[220px] md:w-[260px] mx-auto md:mx-0">
            <div className="relative aspect-[3/4] rounded-xl overflow-hidden">
              <img
                src={comic.cover}
                alt={comic.title}
                className="w-full h-full object-cover"
                width={260}
                height={347}
              />
              {comic.status === '连载中' && (
                <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                  连载中
                </span>
              )}
              {comic.status === '已完结' && (
                <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                  已完结
                </span>
              )}
            </div>
          </div>

          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {comic.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm text-foreground-500">作者：{comic.author}</span>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-500">类型：{categoryMap[comic.category]}</span>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-500">状态：{comic.status}</span>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-500">
                更新：{comic.updatedAt}
              </span>
            </div>

            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                <span className="text-xs text-foreground-500">/ 10</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-eye-line text-foreground-400"></i>
                <span className="text-sm text-foreground-500">
                  {comic.views > 1000000
                    ? `${(comic.views / 10000).toFixed(0)}万`
                    : `${(comic.views / 10000).toFixed(1)}万`}{' '}
                  次阅读
                </span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-file-list-3-line text-foreground-400"></i>
                <span className="text-sm text-foreground-500">共 {comic.chapters.length} 话</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-5">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
              {comic.description}
            </p>

            <div className="flex items-center gap-3">
              <Link
                to={`/comic/${comic.id}?chapter=1`}
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 no-underline whitespace-nowrap"
              >
                开始阅读
              </Link>
              <button className="px-4 py-2.5 border border-background-200/70 text-foreground-600 rounded-lg font-medium text-sm hover:bg-background-100 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-heart-line"></i>
                收藏
              </button>
            </div>
          </div>
        </div>

        {/* Chapter List */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-heading font-bold text-foreground-900">章节列表</h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">共 {comic.chapters.length} 话</span>
              <button className="text-xs text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">
                倒序
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {comic.chapters.map((chapter) => (
              <Link
                key={chapter.id}
                to={`/comic/${comic.id}?chapter=${chapter.number}`}
                className="block px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:bg-primary-50/30 hover:text-primary-600 transition-all duration-200 no-underline text-center truncate"
              >
                {chapter.title}
              </Link>
            ))}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {relatedComics.map((related) => (
                <Link
                  key={related.id}
                  to={`/comic/${related.id}`}
                  className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={related.cover}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      width={200}
                      height={267}
                    />
                  </div>
                  <div className="p-2.5">
                    <h4 className="text-xs font-semibold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                      {related.title}
                    </h4>
                    <p className="text-[11px] text-foreground-500 mt-0.5 truncate">{related.author}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}